import React, { useEffect, useState } from "react";
import { MoveLeft, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import styles from "./NotificationOverview.module.css";
import Header from "components/ui/header/Header";
type BrowserNotificationPermission = NotificationPermission | "unsupported";
type MobileNotificationPermission = "granted" | "denied" | "undetermined";

export const NotificationSettings = () => {
  const navigate = useNavigate();

  const [notificationPermission, setNotificationPermission] =
    useState<BrowserNotificationPermission>("default");

  useEffect(() => {
    if (typeof window === "undefined") return;

    const getFallbackPermission = (): BrowserNotificationPermission => {
      if (!("Notification" in window)) {
        return "unsupported";
      }
      return window.Notification.permission;
    };

    const mapMobilePermissionToBrowserPermission = (
      status: MobileNotificationPermission
    ): BrowserNotificationPermission => {
      if (status === "granted") return "granted";
      if (status === "denied") return "denied";
      return "default";
    };

    const handleNativeMessage = (event: MessageEvent) => {
      let payload: unknown = event.data;

      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }

      if (!payload || typeof payload !== "object") return;

      const parsed = payload as {
        type?: string;
        source?: string;
        data?: { status?: MobileNotificationPermission };
      };

      if (
        parsed.type !== "NOTIFICATION_PERMISSION_STATUS" ||
        parsed.source !== "expo-native"
      ) {
        return;
      }

      const status = parsed.data?.status;
      if (!status) return;

      setNotificationPermission(mapMobilePermissionToBrowserPermission(status));
    };

    setNotificationPermission(getFallbackPermission());
    window.addEventListener("message", handleNativeMessage);

    const syncBrowserPermissionOnFocus = () => {
      setNotificationPermission(getFallbackPermission());
    };

    document.addEventListener("visibilitychange", syncBrowserPermissionOnFocus);

    return () => {
      window.removeEventListener("message", handleNativeMessage);
      document.removeEventListener(
        "visibilitychange",
        syncBrowserPermissionOnFocus
      );
    };
  }, []);

  const openNotificationSettings = () => {
    const rnWebView = (window as any).ReactNativeWebView;
    if (rnWebView?.postMessage) {
      const message = {
        type: "REQUEST",
        action: "OPEN_NOTIFICATION_SETTINGS",
        requestId: `open-notification-settings-${Date.now()}`,
        data: {},
      };
      rnWebView.postMessage(JSON.stringify(message));
      return;
    }

    if (typeof window !== "undefined" && "Notification" in window) {
      window.Notification.requestPermission().then(setNotificationPermission);
    }
  };
  return (
    <div className="root">
      <Header title={"Notifications"} />

      {notificationPermission !== "granted" ? (
        <div className={styles.cardShell}>
          <span className="grid-title">Notifications are not enabled</span>
          <p className={styles.priceLine}>
            Notifications are turned off in your phone settings
          </p>
          <button
            type="button"
            className="proceed-button"
            onClick={openNotificationSettings}
          >
            Go to phone settings
          </button>
        </div>
      ) : (
        <div className={styles.cardShell}>
          <span className="grid-title">Notifications are not enabled</span>
          <p className={styles.priceLine}>Notifications are enabled</p>
        </div>
      )}
    </div>
  );
};
